/* eslint-disable react-hooks/exhaustive-deps */
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import useUrlFilters from '../../../hooks/useUrlFilters'
import useBaseService from '../../../hooks/services/useBaseService'
import useLoader from '../../../hooks/useLoader'
import useNotification from '../../../hooks/useNotification'
import { transformFiltersForUrl } from '../../../utils/transformFiltersForUrl'
import { entities } from '../../../constants/entities'
import useAction from '../../../hooks/useAction'
import MainLayout from '../../../layouts/MainLayout'
import PageHeader from '../../../components/PageHeader'
import TableWithInactiveFilter from '../../../components/table/TableWithInactiveFilter'
import ConfirmationModal from '../../../components/modals/ConfirmationModal'
import { faFileExcel, faFilePdf } from '@fortawesome/free-solid-svg-icons'
import useForm from '../../../hooks/useForm'
import { setDefaultFieldValues } from '../../../utils/objectUtils'
import SimpleBody from '../../../components/forms/SimpleBody'
import SectionHeader from '../../../components/forms/SectionHeader'
import Form from '../../../components/forms/Form'
import { filterFields } from './filters'
import useRemoteSelectOptions from '../../../hooks/useRemoteSelectOptions'
import Button from '../../../components/Button'
import { Alert, Box } from '@mui/material'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import useSelectOptions from '../../../hooks/useSelectOptions'
import { formatDateToInput } from '../../../utils/formatDateToInput'
import useQueryWithLoader from '../../../hooks/useQueryWithLoader'
import { useReport } from '../../../context/ReportContext'

const EquipmentInStock = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const queryClient = useQueryClient()

  const { setExportId, isExporting } = useReport()

  const [filtersSection] = filterFields
  const { formData, handleChange, setFormData } = useForm(
    setDefaultFieldValues([filtersSection]),
    [filtersSection]
  )

  // Se ao menos um tiver preenchido marcar como true
  const [hasFilled, setHasFilled] = useState(false)

  useEffect(() => {
    const hasFilledValidate =
      formData.filter.status_id?.value ||
      formData.filter.organization_id?.value ||
      formData.filter.supplier_id?.value ||
      formData.filter.type_id?.value ||
      formData.filter.order_by?.value
    setHasFilled(hasFilledValidate)
  }, [formData])

  const {
    filters,
    setFilters: applyUrlFilters,
    pagination,
    changePage,
    setPagination,
  } = useUrlFilters(
    {
      ...(formData.filter.status_id?.value && {
        status_id: formData.filter.status_id?.value,
      }),
      ...(formData.filter.organization_id?.value && {
        current_organization_id: formData.filter.organization_id?.value,
      }),
      ...(formData.filter.supplier_id?.value && {
        supplier_id: formData.filter.supplier_id?.value,
      }),
      ...(formData.filter.type_id?.value && {
        type_id: formData.filter.type_id?.value,
      }),
      ...(formData.filter.order_by?.value && {
        column: formData.filter.order_by.value,
        order: formData.filter.order_by.value === 'number' ? 'desc' : 'asc',
      }),
      deleted_at: false,
    },
    'equipmentsInStock'
  )

  const { get: baseServiceGet } = useBaseService(navigate)
  const { hideLoader, showLoader } = useLoader()
  const { showNotification } = useNotification()

  // Cliente contratante
  const statusSelect = useRemoteSelectOptions({
    queryKey: ['status'],
    entityPath: entities.status.get,
    baseServiceGet,
    fieldId: 'filter.status_id',
    formData,
    setFormData,
    perPage: 15,
    initialPage: 1,
    enablePagination: true,
    mapToOption: (item) => ({
      value: item.id,
      label: item.name,
    }),
  })

  const organizationSelect = useRemoteSelectOptions({
    queryKey: ['organizations'],
    entityPath: entities.organizations.get,
    baseServiceGet,
    fieldId: 'filter.organization_id',
    formData,
    mapToOption: (item) => ({
      value: item.id,
      label: `${item.id} - ${item.alias}`,
    }),
    setFormData,
    perPage: 15,
    initialPage: 1,
    enablePagination: true,
  })

  const supplierSelect = useRemoteSelectOptions({
    queryKey: ['suppliers'],
    entityPath: entities.suppliers.get,
    baseServiceGet,
    fieldId: 'filter.supplier_id',
    formData,
    mapToOption: (item) => ({
      value: item.id,
      label: `${item.id} - ${item.alias}`,
    }),
    setFormData,
    perPage: 15,
    initialPage: 1,
    enablePagination: true,
  })

  const typeSelect = useRemoteSelectOptions({
    queryKey: ['types'],
    entityPath: entities.types.get,
    baseServiceGet,
    fieldId: 'filter.type_id',
    formData,
    mapToOption: (item) => ({ value: item.id, label: item.name }),
    setFormData,
    perPage: 15,
    initialPage: 1,
    enablePagination: true,
  })

  const rawOrdeByOptions = useMemo(() => {
    return [
      { label: 'Alfabético', value: 'name' },
      { label: 'Numérico', value: 'number' },
    ]
  }, [])

  const orderBySelect = useSelectOptions({
    fieldId: 'filter.order_by',
    rawOptions: rawOrdeByOptions,
    fetcher: async () => {
      const filters = transformFiltersForUrl({
        deleted_at: false,
      })
      return await baseServiceGet(entities.types.get, {
        filters,
      }).then((res) => res.result.data)
    },
    formData,
    mapToOption: (item) => ({ value: item.value, label: item.label }),
    setFormData,
  })

  const selects = {
    'filter.status_id': statusSelect,
    'filter.organization_id': organizationSelect,
    'filter.supplier_id': supplierSelect,
    'filter.type_id': typeSelect,
    'filter.order_by': orderBySelect,
  }

  const getSelectMeta = useCallback(
    (fieldId) => {
      const selectConfig = selects[fieldId]
      if (!selectConfig) return {}

      return {
        hasNextPage: selectConfig.hasNextPage,
        isFetchingNextPage: selectConfig.isFetchingNextPage,
        isLoading: selectConfig.isLoading,
        isFetching: selectConfig.isFetching,
        handleMenuScrollToBottom: selectConfig.handleMenuScrollToBottom,
        handleSearch: selectConfig.handleSearch,
        onFocus: selectConfig.activateQuery,
      }
    },
    [selects]
  )

  const getOptions = useCallback(
    (fieldId) => {
      const selectConfig = selects[fieldId]
      return selectConfig?.options || []
    },
    [selects]
  )

  const getSelectedValue = useCallback(
    (fieldId) => {
      const selectConfig = selects[fieldId]
      return selectConfig?.selectedValue || null
    },
    [selects]
  )

  const {
    data: equipmentsInStockData,
    isLoading,
    isFetching,
    isError,
    error,
  } = useQueryWithLoader({
    queryKey: ['equipmentsInStock', filters, pagination],
    queryFn: async () => {
      const filtersTransformed = transformFiltersForUrl(filters)
      const params = {
        filters: filtersTransformed,
        page: pagination.page,
        perPage: pagination.perPage,
        sort: {
          column: 'number',
          order: 'desc',
        },
      }
      const response = await baseServiceGet(entities.equipments.get, params)
      return response.result
    },
    staleTime: 0,
    keepPreviousData: true,
    select: (data) => {
      return {
        equipmentsInStock: data?.data?.map((equipment) => ({
          id: equipment.id,
          equipment: [equipment.number, equipment.name]
            .filter(Boolean)
            .join(' - '),
          type: equipment.product?.type?.name ?? '-',
          date_transaction: formatDateToInput(
            equipment?.creation_transaction?.transaction_date
          ),
          warranty_date: equipment?.creation_transaction_item?.warranty_date,
          situation: equipment.status?.name ?? '-',
        })),
        totalPages: data.last_page,
      }
    },
    placeholderData: (prev) => prev,
    refetchOnWindowFocus: false,
  })

  const { equipmentsInStock = [], totalPages = 1 } = equipmentsInStockData || {}

  // Limpa o data se nenhum filtro tiver selecionado
  useEffect(() => {
    if (!hasFilled) {
      queryClient.setQueryData(['equipmentsInStock', filters, pagination], {
        equipmentsInStock: [],
        totalPages: 1,
      })
    }
  }, [hasFilled])

  const columns = [
    { key: 'equipment', label: 'Equipamento', width: '40%' },
    { key: 'type', label: 'Tipo', width: '20%' },
    { key: 'situation', label: 'Situação', width: '10%' },
    { key: 'date_transaction', label: 'D. Aquisição' },
    { key: 'warranty_date', label: 'D. Garantia' },
  ]

  useEffect(() => {
    isLoading ? showLoader() : hideLoader()
  }, [isLoading, showLoader, hideLoader])

  useEffect(() => {
    if (isError) {
      showNotification(
        'error',
        `Erro ao carregar faturamentos: ${
          error.response?.data?.message || error.message
        }`
      )
    }
  }, [isError, error, showNotification])

  // Limpa a URL removendo todos os parâmetros de busca
  useEffect(() => {
    navigate(location.pathname, { replace: true })

    // Opcional: Se você quiser manter apenas a paginação padrão, use:
    // navigate(`${location.pathname}?equipmentsInStock_page=1&equipmentsInStock_perPage=25`, { replace: true });
  }, [])

  const exportReport = useMutation({
    mutationFn: async (type) => {
      showLoader()
      const params = {
        filters: transformFiltersForUrl({
          ...(formData.filter.status_id?.value && {
            status_id: formData.filter.status_id?.value,
          }),
          ...(formData.filter.organization_id?.value && {
            organization_id: formData.filter.organization_id?.value,
          }),
          ...(formData.filter.supplier_id?.value && {
            supplier_id: formData.filter.supplier_id?.value,
          }),
          ...(formData.filter.type_id?.value && {
            type_id: formData.filter.type_id?.value,
          }),
          ...(formData.filter.order_by?.value && {
            column: formData.filter.order_by.value,
            order: formData.filter.order_by.value === 'number' ? 'desc' : 'asc',
          }),
        }),
      }
      const response = await baseServiceGet(
        entities.reports.equipmentInStock.get,
        params,
        {
          type,
        }
      )
      return response
    },
    onMutate: () => {
      showLoader()
    },
    onSuccess: (response) => {
      if (response?.status === 202 || response?.status === 200) {
        const generated_id = response.result?.export_id

        if (generated_id) {
          setExportId(generated_id)
          localStorage.setItem('report_exportId', generated_id.toString())
          showNotification(
            'info',
            'Processamento do relatório iniciado em segundo plano.'
          )
        }
      }
    },
    onError: (error) => {
      console.error('onError ', error)
      hideLoader()
    },
    onSettled: () => {
      hideLoader()
    },
  })

  const {
    openModalConfirmation,
    handleConfirmAction,
    handleCancelConfirmation,
    selectedItem,
    action,
  } = useAction(navigate)

  useEffect(() => {
    if (location.state?.message) {
      showNotification('error', location.state.message)
      navigate(location.pathname, { replace: true, state: {} })
    }
  }, [location.state, showNotification, navigate, location.pathname])

  return (
    <MainLayout selectedCompany="ALUCOM">
      {/* Ajuste do titulo da página a pedido de Dona Delne -> "Equipamentos em Estoque" - "Equipamentos por Situação" */}
      <PageHeader title="Equipamentos por Situação" showBackButton={false} />
      <Alert
        severity="info"
        sx={{
          mb: 2,
        }}>
        <strong>Atenção:</strong> Esse relatório busca todos os equipamentos por
        situação na empresa.
      </Alert>
      <div className="container-fluid p-1">
        <Form
          initialFormData={formData}
          onSubmit={() => {}}
          textSubmit="Filtrar"
          textLoadingSubmit="Filtrando..."
          showButtons={false}
          disabledSubmitButton={true}>
          {() => (
            <>
              <div key={filtersSection.section}>
                <SectionHeader section={filtersSection.section} />

                <SimpleBody
                  fields={filtersSection.fields}
                  formErrors={{}}
                  formData={formData}
                  handleFieldChange={handleChange}
                  getOptions={getOptions}
                  getSelectedValue={getSelectedValue}
                  getSelectMeta={getSelectMeta}
                />
              </div>

              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'flex-end',
                  my: 4,
                  width: '100%',
                }}>
                <Button
                  variant="outlined"
                  color="primary"
                  type="button"
                  startIcon={<FontAwesomeIcon icon={faFilePdf} />}
                  text="Exportar em PDF"
                  disabled={
                    !hasFilled || equipmentsInStock.length === 0 || isExporting
                  }
                  onClick={async () => {
                    try {
                      await exportReport.mutateAsync('pdf')
                    } catch (error) {
                      console.error('Erro ao exportar PDF:', {
                        status: error.status,
                        message: error.message,
                      })
                    }
                  }}
                />
                <Button
                  variant="contained"
                  color="primary"
                  type="button"
                  startIcon={<FontAwesomeIcon icon={faFileExcel} />}
                  text="Exportar em EXCEL"
                  disabled={
                    !hasFilled || equipmentsInStock.length === 0 || isExporting
                  }
                  onClick={async () => {
                    try {
                      await exportReport.mutateAsync('excel')
                    } catch (error) {
                      console.error('Erro ao exportar EXCEL:', {
                        status: error.status,
                        message: error.message,
                      })
                    }
                  }}
                />
              </Box>
            </>
          )}
        </Form>

        <TableWithInactiveFilter
          data={equipmentsInStock}
          columns={columns}
          totalPages={totalPages}
          currentPage={pagination.page - 1}
          onPageChange={changePage}
          rowsPerPage={pagination.perPage}
          applyUrlFilters={applyUrlFilters}
          onRowsPerPageChange={(event) => {
            const newPerPage = +event.target.value
            setPagination({ page: 1, perPage: newPerPage })
          }}
          type="default"
          showFilterInactive={false}
          isLoading={isLoading}
          isFetching={isFetching || isExporting}
        />

        <ConfirmationModal
          open={openModalConfirmation}
          onClose={handleCancelConfirmation}
          onConfirm={handleConfirmAction}
          itemName={
            `N° do Movimento (${selectedItem?.number}) - Cliente: ${selectedItem?.customer}` ||
            ''
          }
          text={action?.text}
        />
      </div>
    </MainLayout>
  )
}

export default EquipmentInStock
